import classNames from "classnames";
import * as React from "react";
import { Keys } from "~/common/enums";
import { closeModalPopupEvent } from "~/sharedComponents/modalPopup/events";
import { CloseCallback, CloseReason } from "../../sharedHOC/ClickOutside/ClickOutside";
import { renderTarget as renderDefaultTarget } from "../../utils/renderTarget";
import { understandChildren } from "../../utils/understandChildren";
import { InlinePopup, InlinePopupProps } from "../inlinePopup/InlinePopup";
import * as styles from "./Popover.less";
import { PopoverContext } from "./PopoverContext";
import { PopoverPopupStateContext, PopupState } from "./PopoverPopupStateContext";
import { PopupTriggerProps, RenderContentProps } from "./types";

type PopoverInlinePopupProps = Partial<
    Pick<
        InlinePopupProps,
        | "placement"
        | "fallbackPlacements"
        | "strategy"
        | "showOverTrigger"
        | "showWithoutIndent"
        | "blockClicksOutside"
        | "width"
        | "ignorePreventedEsc"
        | "enableDropDownBehavior"
        | "matchTriggerWidth"
        | "minWidth"
        | "handleFocusedElementUnmounting"
        | "disableClosing"
        | "disableClosingIfChildPopupOpened"
    >
>;

export const enum PopoverInteractionKinds {
    /**
     * Popover
     *  - opens when trigger btn is clicked.
     *  - closes when user clicks outside of the popover (including the trigger btn).
     *       Also in this mode popover is closed when user removed focus from popover.
     */
    click = "click",
    /**
     * Popover
     *  - opens when trigger btn is clicked.
     *  - closes when trigger btn is clicked.
     */
    clickTriggerOnly = "clickTargetOnly",
}

export type PopoverProps = RenderContentProps &
    PopoverInlinePopupProps & {
        /**
         * Whether the popup should acquire focus when it opens.
         * @default true
         */
        autoFocus?: boolean;
        /**
         * Whether the popup should prevent focus from leaving itself.
         * @default true
         */
        enforceFocus?: boolean;
        /**
         * Adds specified class to the wrapped div.
         * Used to apply some styles for popup's trigger only when popup is opened.
         */
        openedClass?: string;
        /**
         * if true then popup won't be opened on trigger click.
         */
        disabled?: boolean;

        /**
         * Callback executed whenever popover is opened or closed or should be closed in controlled mode.
         */
        onToggle?: (newValue: boolean, data?: { closeReason: CloseReason }) => any;

        /**
         * Class that is added to the trigger/target element wrapper.
         */
        wrapperClassName?: string;
        popupClassName?: string;
        renderToBody?: boolean;
        /**
         * Can be used to hide popup until data was not loaded (in case if data
         * loading is started when popup is opened first time)
         */
        hidePopup?: boolean;

        openInitially?: boolean;

        /**
         *  The kind of interaction that changes popover visibility.
         *  @default PopoverInteractionKinds.click
         */
        interactionKind?: PopoverInteractionKinds;

        /**
         * Whether the popover is visible. Passing this prop puts the popover in
         * **controlled mode**, where the only way to change visibility is by updating
         * this property.
         * See also **onInteraction** prop.
         */
        isOpen?: boolean;

        /**
         * Might be used to prevent closing "parent" widgets implemented on jquery,
         * for example task/project details, jquery dialogs and etc.
         */
        preventJqueryEsc?: boolean;
        closeOnSearch?: boolean;
        /**
         * If true, popup won't close when the trigger is clicked.
         * False by default.
         */
        dontCloseOnTriggerClick?: boolean;
        /**
         * If specified, the target won't be cloned and won't be wrapped with any additional elements to set the ref on.
         * Instead, the provided target must manage triggertRef itself and apply passed key/mouse handlers.
         * Please note that wrapperClassName, openedClass won't be applied in this case.
         */
        renderTrigger?: (props: PopupTriggerProps) => JSX.Element;
    };

interface PopoverState {
    isOpened: boolean;
}

export class Popover extends React.Component<PopoverProps, PopoverState> {
    context!: React.ContextType<typeof PopoverContext>;

    static CLOSE_CLASS = "CLOSE_POPUP";
    static BLOCK_WRAPPER_CLASS = styles.block_wrapper;

    /**
     * Prevents popover opening if element with this class inside trigger was clicked
     */
    static TRIGGER_DISMISS = "TRIGGER_DISMISS";

    static defaultProps: Partial<PopoverProps> = {
        autoFocus: true,
        enforceFocus: true,
        interactionKind: PopoverInteractionKinds.click,
        ignorePreventedEsc: true,
    };
    isLastKeyDownWasEnter: boolean;
    targetWrapperElRef: React.RefObject<HTMLDivElement> = React.createRef();
    previousFocusedElement: HTMLElement;
    _isMounted: boolean = true;
    _lastCloseReason: CloseReason = null;

    constructor(props: PopoverProps) {
        super(props);

        this.state = { isOpened: false };
    }

    private get isControlledMode() {
        return this.props.isOpen !== undefined;
    }

    get isOpened() {
        return this.isControlledMode ? this.props.isOpen : this.state.isOpened;
    }

    getRenderToBody() {
        const { renderToBody } = this.props;
        if (renderToBody !== undefined) {
            return renderToBody;
        }

        return this.context.defaultRenderToBody;
    }

    handleTargetClick = (e?: React.SyntheticEvent) => {
        if (this.props.disabled) {
            return;
        }

        const target = (e && e.target) as HTMLElement;
        if (!this.isOpened && target && target.closest(`.${Popover.TRIGGER_DISMISS}`)) {
            return;
        }

        if (this.isOpened && this.props.dontCloseOnTriggerClick) {
            return;
        }

        this.previousFocusedElement = document.activeElement as HTMLElement;

        this.setIsOpened(!this.isOpened, this.isOpened ? CloseReason.CLICK_OUTSIDE : undefined);
    };

    setIsOpened = (newState: boolean, closeReason?: CloseReason) => {
        if (!this.isControlledMode) {
            this.setState({ isOpened: newState });
            this.props.onToggle?.(newState, { closeReason });
        } else {
            this.props.onToggle?.(newState, { closeReason });
        }
    };

    handleTargetKeyUp = (event: React.KeyboardEvent) => {
        // opens popup only on keyup but additionally checks that corresponding keydown
        // also was pressed on target because it might leads to bug if content was closed
        // by enter keydown (for example selection in InlinePopupSelect inside content that leads
        // to popup closing)
        if (event.key === Keys.Enter && this.isLastKeyDownWasEnter) {
            this.handleTargetClick(event);
        }

        this.isLastKeyDownWasEnter = false;
    };

    handleTargetKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === Keys.Enter) {
            this.isLastKeyDownWasEnter = true;
        }
    };

    closePopup = (reason: CloseReason) => {
        this._lastCloseReason = reason;
        this.setIsOpened(false, reason);

        if (
            reason === CloseReason.ESC ||
            reason === CloseReason.CLICK_INSIDE ||
            (reason === CloseReason.CLICK_OUTSIDE && this.props.blockClicksOutside !== false) ||
            (reason === CloseReason.TAB && this.props.renderToBody)
        ) {
            if (this.previousFocusedElement) {
                this.previousFocusedElement.focus();
                this.previousFocusedElement = null;
            }
        } else {
            this.previousFocusedElement = null;
        }
    };

    handlePopupClose: CloseCallback = (reason, event) => {
        if (
            (typeof this.props.disableClosing === "function"
                ? this.props.disableClosing(event as any)
                : this.props.disableClosing) ||
            !this.isMatchToInteractionKind(reason)
        ) {
            return;
        }

        this.closePopup(reason);

        if (event && reason === CloseReason.ESC) {
            event.preventDefault();
        }
    };

    private isMatchToInteractionKind(reason: CloseReason) {
        if (this.props.interactionKind === PopoverInteractionKinds.clickTriggerOnly) {
            return reason === CloseReason.ESC || reason === CloseReason.CLICK_INSIDE;
        }

        return true;
    }

    handleContentClick = (event: React.SyntheticEvent) => {
        const target = event.target as HTMLElement;
        if (target.closest(`.${Popover.CLOSE_CLASS}`)) {
            this.handlePopupClose(CloseReason.CLICK_INSIDE);
        }
    };

    handleContentKeyUp = (event: React.KeyboardEvent) => {
        if (event.key === Keys.Enter) {
            this.handleContentClick(event);
        }
    };

    renderTarget() {
        const { children, renderContent, renderTrigger } = this.props;

        if (renderTrigger) {
            return this.props.renderTrigger({
                onClick: this.handleTargetClick,
                onKeyDown: this.handleTargetKeyDown,
                onKeyUp: this.handleTargetKeyUp,
                active: this.isOpened,
                triggerRef: this.targetWrapperElRef,
                disabled: this.props.disabled,
            });
        }

        const { target } = understandChildren(children, renderContent, renderTrigger);

        return renderDefaultTarget({
            sourceTarget: target,
            onClick: this.handleTargetClick,
            onKeyDown: this.handleTargetKeyDown,
            onKeyUp: this.handleTargetKeyUp,
            active: this.isOpened,
        });
    }

    closePopupFromContent = (closeReason = CloseReason.ESC) => {
        this.handlePopupClose(closeReason);
    };

    closePopupFromSearch = () => {
        this.previousFocusedElement = null;
        this.closePopup(CloseReason.CLICK_OUTSIDE);
    };

    renderContent() {
        const { children, renderContent, renderTrigger } = this.props;
        if (renderContent) {
            return renderContent({
                close: this.closePopupFromContent,
            });
        }

        const { content } = understandChildren(children, renderContent, renderTrigger);
        return content;
    }

    getPopupState = (): PopupState => ({
        lastCloseReason: this._lastCloseReason,
        close: this.closePopupFromContent,
    });

    render() {
        const {
            placement,
            fallbackPlacements,
            strategy,
            showOverTrigger,
            showWithoutIndent,
            autoFocus,
            enforceFocus,
            width,
            minWidth,
            matchTriggerWidth,
        } = this.props;

        const renderToBody = this.getRenderToBody();

        return (
            <>
                {this.props.renderTrigger && this.renderTarget()}
                <div
                    className={classNames(
                        styles.wrapper,
                        this.isOpened && this.props.openedClass,
                        this.props.wrapperClassName
                    )}
                    onKeyUp={this.handleContentKeyUp}
                    ref={!this.props.renderTrigger && this.targetWrapperElRef}
                    onClick={this.handleContentClick}
                >
                    {!this.props.renderTrigger && this.renderTarget()}
                    <InlinePopup
                        preventJqueryEsc={this.props.preventJqueryEsc}
                        isOpen={this.isOpened && !this.props.hidePopup}
                        placement={placement}
                        fallbackPlacements={fallbackPlacements}
                        strategy={strategy}
                        blockClicksOutside={this.props.blockClicksOutside}
                        showOverTrigger={showOverTrigger}
                        showWithoutIndent={showWithoutIndent}
                        triggerElRef={this.targetWrapperElRef}
                        autoFocus={autoFocus}
                        enforceFocus={enforceFocus}
                        className={this.props.popupClassName}
                        width={width}
                        minWidth={minWidth}
                        matchTriggerWidth={matchTriggerWidth}
                        closeActionHandler={this.handlePopupClose}
                        handleFocusedElementUnmounting={this.props.handleFocusedElementUnmounting}
                        ignorePreventedEsc={this.props.ignorePreventedEsc}
                        renderToBody={renderToBody}
                        enableDropDownBehavior={this.props.enableDropDownBehavior}
                        disableClosing={this.props.disableClosing}
                        disableClosingIfChildPopupOpened={this.props.disableClosingIfChildPopupOpened}
                    >
                        <PopoverPopupStateContext.Provider value={this.getPopupState}>
                            {this.renderContent()}
                        </PopoverPopupStateContext.Provider>
                    </InlinePopup>
                </div>
            </>
        );
    }

    componentDidMount() {
        if (this.props.openInitially && !this.props.disabled) {
            this.setIsOpened(true);
        }

        if (this.props.closeOnSearch) {
            window.addEventListener(closeModalPopupEvent, this.closePopupFromSearch);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        if (this.props.closeOnSearch) {
            window.removeEventListener(closeModalPopupEvent, this.closePopupFromSearch);
        }
    }
}

Popover.contextType = PopoverContext;
